import type { MouseEvent, ReactElement, ReactNode } from "react";

import { useTracking } from "react-tracking";

import { BuiMenu } from "@btnet/blueprint-components";
import { IconCaretSmallDown } from "@btnet/blueprint-icons";

import {
  BTButton,
  type BTButtonType,
  type IBTButtonProps,
} from "../BTButton/BTButton";
import { BTDropdown, type IBTDropdownProps } from "../BTDropdown/BTDropdown";

declare type OverlayFunc = () => ReactElement;

/** A single menu item used to populate the BTDropdownButton's menu. */
export interface BTDropdownMenuItem {
  "data-testid": string;
  onClick: () => Promise<void> | void;
  disabled?: boolean;
  content: ReactNode;
  "aria-label"?: string;
}

/**
 * Shared BTDropdownButton props, minus the menu source (`menuItems` / `overlay`),
 * which is modelled as a discriminated union below.
 */
interface IBTDropdownButtonBaseProps<ActionType>
  extends Omit<IBTDropdownProps, "overlay" | "testid"> {
  "data-testid": string;
  type?: BTButtonType;
  isolated?: boolean;
  children?: ReactNode;
  actionBeingPerformed?: ActionType;
  loadingAction?: ActionType;
  /** Props forwarded to the trigger button itself. */
  buttonProps?: Partial<IBTButtonProps<ActionType>>;
  /**
   * Whether the caret is shown after the dropdown label.
   * @default true
   */
  showCaret?: boolean;
  onClick?: (e: MouseEvent) => void;
}

/**
 * BTDropdownButton props. The menu source is a discriminated union: a caller must
 * provide EITHER `menuItems` (a single item collapses to a plain button) OR a
 * custom `overlay` — never neither — so an empty popup is a compile error.
 */
type IBTDropdownButtonProps<ActionType> =
  | (IBTDropdownButtonBaseProps<ActionType> & {
      /** Menu items used to populate the dropdown menu. If exactly one item is passed, the dropdown collapses to a plain button. */
      menuItems: BTDropdownMenuItem[];
      overlay?: never;
    })
  | (IBTDropdownButtonBaseProps<ActionType> & {
      menuItems?: never;
      /** A custom overlay to render instead of a menu generated from `menuItems`. */
      overlay: ReactElement | OverlayFunc;
    });

/**
 * BTDropdownButton component - the core wrapper composing BTDropdown + BTButton.
 *
 * Adds the cross-layer analytics seam: tracking is consumed from `react-tracking`
 * via React context (declared a peer dependency), never imported from the app's
 * analytics utility.
 *
 * Behavior parity with the legacy wrapper:
 * - When exactly one `menuItems` entry is passed, the dropdown collapses to a plain
 *   button firing that item's onClick.
 * - Otherwise renders a BTButton trigger (with an optional caret) that opens the menu;
 *   the BTDropdown wrapper emits the `OpenMenu` analytics event.
 * - Supports the button loading state via `actionBeingPerformed`/`loadingAction`.
 * @param props - The component props.
 * @example
 * ```tsx
 * <BTDropdownButton
 *   data-testid="actions"
 *   menuItems={[
 *     { "data-testid": "edit", content: "Edit", onClick: handleEdit },
 *     { "data-testid": "delete", content: "Delete", onClick: handleDelete },
 *   ]}
 * >
 *   Actions
 * </BTDropdownButton>
 * ```
 */
export function BTDropdownButton<ActionType>(
  props: IBTDropdownButtonProps<ActionType>,
) {
  const {
    children,
    type = "secondary",
    "data-testid": dataTestId,
    overlay,
    menuItems,
    showCaret = true,
    actionBeingPerformed,
    loadingAction,
    buttonProps,
    isolated: _isolated,
    ...otherProps
  } = props;

  const { Track } = useTracking<Record<string, unknown>>({
    component: "Dropdown Actions",
    uniqueId: dataTestId,
  });

  // Only forward loading props as a pair — BTButton's loading variant requires both,
  // and strict `exactOptionalPropertyTypes` forbids passing them as explicit undefined.
  const loadingProps =
    actionBeingPerformed !== undefined && loadingAction !== undefined
      ? { actionBeingPerformed, loadingAction }
      : {};

  // Single-item collapse: render a plain button instead of a dropdown.
  const singleItem =
    menuItems && menuItems.length === 1 ? menuItems[0] : undefined;
  if (singleItem) {
    return (
      <BTButton<ActionType>
        data-testid={singleItem["data-testid"]}
        onClick={singleItem.onClick}
        {...(otherProps.className ? { className: otherProps.className } : {})}
        {...(buttonProps ?? {})}
        {...loadingProps}
        disabled={singleItem.disabled ?? false}
        type={type}
        aria-label={singleItem["aria-label"]}
      >
        {singleItem.content}
      </BTButton>
    );
  }

  // The props union guarantees exactly one of `menuItems` / `overlay` is provided.
  // Destructuring erases that correlation, so re-assert it at runtime — this also
  // narrows `overlay` to non-undefined in the else branch below (no `!` needed).
  if (!menuItems && !overlay) {
    throw new Error(
      "BTDropdownButton requires either `menuItems` or `overlay`.",
    );
  }

  const resolvedOverlay = menuItems ? (
    <BuiMenu testid="dropdownActionsMenu">
      {menuItems.map((x) => (
        <BuiMenu.Item
          key={x["data-testid"]}
          onClick={x.onClick}
          testid={x["data-testid"]}
          disabled={x.disabled ?? false}
          aria-label={x["aria-label"]}
        >
          {x.content}
        </BuiMenu.Item>
      ))}
    </BuiMenu>
  ) : (
    overlay
  );

  let buttonLabel: ReactNode = undefined;
  if (children || showCaret) {
    buttonLabel = (
      <>
        {children}
        {showCaret && <IconCaretSmallDown />}
      </>
    );
  }

  return (
    <Track>
      <BTDropdown {...otherProps} testid={dataTestId} overlay={resolvedOverlay}>
        <BTButton<ActionType>
          data-testid={dataTestId}
          type={type}
          disabled={otherProps.disabled ?? false}
          {...(buttonProps ?? {})}
          {...loadingProps}
        >
          {buttonLabel}
        </BTButton>
      </BTDropdown>
    </Track>
  );
}
